import { z } from 'zod';
export const ClientTypeSchema = z.enum(['PERSONA', 'EMPRESA']);
// Formato de NIF/DNI/NIE/CIF en España (sin validar checksum):
// DNI:  8 dígitos + 1 letra
// NIE:  [XYZ] + 7 dígitos + 1 letra
// CIF:  1 letra de control + 7 dígitos + (1 dígito o 1 letra)
export const NIF_REGEX = /^([0-9]{8}[A-Z]|[XYZ][0-9]{7}[A-Z]|[A-HJNPQRSUVW][0-9]{7}[0-9A-J])$/;
const optionalString = (max = 200) => z.string().max(max).nullable().optional();
const ClientBaseSchema = z.object({
    type: ClientTypeSchema,
    // Persona física
    firstName: optionalString(100),
    lastName1: optionalString(100),
    lastName2: optionalString(100),
    // Persona jurídica
    legalName: optionalString(300),
    // Identificación fiscal
    nationalId: z
        .string()
        .max(20)
        .nullable()
        .optional()
        .transform((v) => (v ? v.toUpperCase().trim() : v))
        .refine((v) => !v || NIF_REGEX.test(v), 'Formato de NIF/DNI/NIE/CIF no válido para España'),
    email: z
        .union([z.string().email('Email no válido'), z.literal('')])
        .nullable()
        .optional(),
    phone: optionalString(30),
    mobile: optionalString(30),
    address: optionalString(300),
    city: optionalString(100),
    postalCode: optionalString(10),
    province: optionalString(100),
    country: z.string().length(2).optional(),
    notes: z.string().max(5000).nullable().optional(),
    /** A4: emisor con el que se le factura por defecto. */
    issuerId: z.string().nullable().optional(),
});
export const CreateClientSchema = ClientBaseSchema.superRefine((data, ctx) => {
    if (data.type === 'PERSONA') {
        if (!data.firstName || !data.lastName1) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['firstName'],
                message: 'Indica al menos nombre y primer apellido',
            });
        }
    }
    else if (data.type === 'EMPRESA') {
        if (!data.legalName) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['legalName'],
                message: 'Indica la razón social',
            });
        }
    }
});
export const UpdateClientSchema = ClientBaseSchema.partial({ type: true });
export const ClientListItemSchema = z.object({
    id: z.string(),
    type: ClientTypeSchema,
    displayName: z.string(),
    nationalId: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    city: z.string().nullable(),
    createdAt: z.string().datetime(),
});
export const ClientDetailSchema = ClientListItemSchema.extend({
    firstName: z.string().nullable(),
    lastName1: z.string().nullable(),
    lastName2: z.string().nullable(),
    legalName: z.string().nullable(),
    mobile: z.string().nullable(),
    address: z.string().nullable(),
    postalCode: z.string().nullable(),
    province: z.string().nullable(),
    country: z.string(),
    notes: z.string().nullable(),
    issuerId: z.string().nullable(),
    createdById: z.string().nullable(),
    createdByName: z.string().nullable(),
    updatedAt: z.string().datetime(),
});
export const ClientListQuerySchema = z.object({
    q: z.string().max(200).optional(),
    type: ClientTypeSchema.optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export const ClientListResponseSchema = z.object({
    items: z.array(ClientListItemSchema),
    total: z.number().int(),
    page: z.number().int(),
    pageSize: z.number().int(),
});
/** Compone el displayName a partir de los campos de un cliente. */
export function clientDisplayName(input) {
    if (input.type === 'EMPRESA')
        return input.legalName?.trim() || '';
    return [input.firstName, input.lastName1, input.lastName2]
        .map((s) => s?.trim() ?? '')
        .filter(Boolean)
        .join(' ');
}
//# sourceMappingURL=clients.js.map